import moment from 'moment-timezone'

import { Periods, SchedulingMethods } from 'constants/meetings'
import { INVALID_SUPERGRAPH_SCOPE_TYPES, isValidSupergraphScopeType } from 'features/meetings/supergraph/scope_utils'
import { STATUS_MAP } from 'services/search_api/constants'
import { mapScopeTypeToBackendValue } from 'shared/api/meetings/utils'

/**
 * Calendly + Google + Outlook. Used by the `meetingsPageList` (search-api) path, which still keys off
 * `kinds`; cmd `listMeetings` keys off `sources` instead (see {@link buildListMeetingsSources}).
 */
export const DEFAULT_SUPERGRAPH_MEETING_KINDS = Object.freeze(['CALENDLY', 'GOOGLE', 'OUTLOOK'])

/**
 * Routing predicate for the supergraph meetings list. cmd `listMeetings` cannot express keyword search
 * or the recap (meeting-intelligence) filter, so those route to the search-then-filter `meetingsPageList`.
 */
export const shouldUseMeetingsPageList = (uiFilters) => {
  if (!uiFilters) return false
  const hasKeyword = Boolean(uiFilters.searchKeyword?.trim?.())
  const hasRecapFilter = Boolean(uiFilters.meetingIntelligenceStatus)
  return hasKeyword || hasRecapFilter
}

const buildSupergraphKindsFromUiSchedulingMethods = (schedulingMethods) => {
  if (schedulingMethods == null) {
    return [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  if (!Array.isArray(schedulingMethods) || schedulingMethods.length === 0) {
    return [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  if (schedulingMethods.includes(SchedulingMethods.ALL)) {
    return [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  const kinds = []
  if (schedulingMethods.includes(SchedulingMethods.CALENDLY)) kinds.push('CALENDLY')
  if (schedulingMethods.includes(SchedulingMethods.GOOGLE)) kinds.push('GOOGLE')
  if (schedulingMethods.includes(SchedulingMethods.OUTLOOK)) kinds.push('OUTLOOK')
  if (kinds.length === 0) {
    return [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  return kinds
}

export const applyFixedMeetingsOrganizerScope = (filter) => {
  filter.is_organizer = false
  filter.include_notetaker_ineligible_external_events = true
}

export const buildSupergraphTimeFilter = (uiFilters, { today, past, forCount }) => {
  if (!uiFilters) return null
  const { period, startDate, endDate } = uiFilters

  const addDateRangeFilterForCount = (rangeStart, rangeEnd) => ({
    operation: 'BETWEEN',
    start: moment(rangeStart).format('YYYY-MM-DDTHH:mm:ssZ'),
    end: moment(rangeEnd).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
  })

  if (forCount) {
    switch (period) {
      case Periods.UPCOMING:
        return null
      case Periods.FIXED:
        if (!startDate || !endDate) return null
        return addDateRangeFilterForCount(startDate, endDate)
      case Periods.THIS_WEEK:
        return addDateRangeFilterForCount(moment().startOf('week'), moment().endOf('week'))
      case Periods.THIS_MONTH:
        return addDateRangeFilterForCount(moment().startOf('month'), moment().endOf('month'))
      case Periods.THIS_YEAR:
        return addDateRangeFilterForCount(moment().startOf('year'), moment().endOf('year'))
      case Periods.TODAY:
        return addDateRangeFilterForCount(moment().startOf('day'), moment().endOf('day'))
      default:
        return null
    }
  }

  const todayStart = moment().startOf('day')
  const startOfTodayStr = todayStart.format('YYYY-MM-DDTHH:mm:ssZ')
  const endOfTodayStr = moment().endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')

  const addDateRangeFilter = (rangeStart, rangeEnd) => {
    const formattedStartDate = moment(rangeStart).format('YYYY-MM-DDTHH:mm:ssZ')
    const formattedEndDate = moment(rangeEnd).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')
    if (past) {
      const actualEndDate = moment(rangeEnd).isBefore(todayStart)
        ? formattedEndDate
        : endOfTodayStr
      return {
        operation: 'BETWEEN',
        start: formattedStartDate,
        end: actualEndDate,
      }
    }
    const actualStartDate = moment(rangeStart).isAfter(todayStart)
      ? formattedStartDate
      : startOfTodayStr
    return {
      operation: 'BETWEEN',
      start: actualStartDate,
      end: formattedEndDate,
    }
  }

  switch (period) {
    case Periods.UPCOMING: {
      if (today) {
        return {
          operation: 'BETWEEN',
          start: startOfTodayStr,
          end: endOfTodayStr,
        }
      }
      if (past) {
        return { operation: 'LT', start: startOfTodayStr }
      }
      return { operation: 'GTE', start: endOfTodayStr }
    }
    case Periods.FIXED:
      if (!startDate || !endDate) return null
      return addDateRangeFilter(startDate, endDate)
    case Periods.THIS_WEEK:
      return addDateRangeFilter(moment().startOf('week'), moment().endOf('week'))
    case Periods.THIS_MONTH:
      return addDateRangeFilter(moment().startOf('month'), moment().endOf('month'))
    case Periods.THIS_YEAR:
      return addDateRangeFilter(moment().startOf('year'), moment().endOf('year'))
    case Periods.TODAY:
      return addDateRangeFilter(moment().startOf('day'), moment().endOf('day'))
    default:
      return null
  }
}

/**
 * Snake-cased filter for the search-api `meetingsPageList` path (`MeetingsSearch_MeetingsFilter`).
 * Used by the keyword-search / recap / count builders in `meeting_search_supergraph_utils`.
 */
export const buildSupergraphFilterFromUiFilters = (uiFilters, { today, past, forCount } = {}) => {
  if (!uiFilters) return null

  const {
    inviteeEmails,
    eventTypeIds,
    userIds,
    teamIds,
    role,
    ownerType,
    searchKeyword,
    meetingIntelligenceStatus,
    statusIds,
    schedulingMethods,
    scopeType,
    scopeId,
    uuid,
  } = uiFilters

  const filter = {}

  if (inviteeEmails?.length) filter.invitee_emails = inviteeEmails
  if (eventTypeIds?.length) filter.event_type_uuids = eventTypeIds
  if (userIds?.length) filter.user_uuids = userIds
  if (teamIds?.length) filter.team_uuids = teamIds

  if (role) filter.role = role
  if (ownerType) filter.owner_type = ownerType

  if (meetingIntelligenceStatus) {
    filter.meeting_intelligence_status = meetingIntelligenceStatus
  }
  if (searchKeyword) filter.search_keyword = searchKeyword

  const mappedFromRoute = mapScopeTypeToBackendValue(scopeType, scopeId)
  if (mappedFromRoute === 'all_users_and_teams') {
    if (scopeId) filter.scope_id = scopeId
  } else if (mappedFromRoute) {
    if (!INVALID_SUPERGRAPH_SCOPE_TYPES.has(mappedFromRoute)) {
      filter.scope_type = mappedFromRoute
      if (scopeId && mappedFromRoute !== 'my_calendly' && scopeId !== 'me') {
        filter.scope_id = scopeId
      }
    }
  } else if (isValidSupergraphScopeType(scopeType)) {
    filter.scope_type = scopeType
    if (scopeId) filter.scope_id = scopeId
  }

  if (uuid) filter.ids = [uuid]

  if (statusIds?.length) {
    const mapped = statusIds
      .map((id) => STATUS_MAP[id])
      .filter(Boolean)

    if (mapped.length === 1) {
      filter.status = mapped[0]
    } else if (mapped.length > 1) {
      filter.statuses = mapped
    }
  }

  filter.kinds = buildSupergraphKindsFromUiSchedulingMethods(schedulingMethods)

  const timeFilter = buildSupergraphTimeFilter(uiFilters, { today, past, forCount })
  if (timeFilter) filter.start_time = timeFilter

  applyFixedMeetingsOrganizerScope(filter)
  return filter
}

/** cmd `listMeetings` scope kind (`Meetings_ListMeetingsScopeType`) keyed by backend scope value. */
const LIST_MEETINGS_SCOPE_KIND_BY_BACKEND_VALUE = {
  user_single: 'USER_SINGLE',
  page_single: 'PAGE_SINGLE',
  user_group: 'USER_GROUP',
}

/**
 * cmd `Scope { id: ID!, kind: ListMeetingsScopeType! }`. Only the id-bearing scopes map; my_calendly
 * and org-wide scopes (all_users / all_users_and_teams / page) have no required id and are omitted so
 * cmd-api defaults to the requesting user's view.
 */
const buildListMeetingsScope = (scopeType, scopeId) => {
  const mapped = mapScopeTypeToBackendValue(scopeType, scopeId)
  const kind = LIST_MEETINGS_SCOPE_KIND_BY_BACKEND_VALUE[mapped]
  if (!kind) return undefined
  if (scopeId == null || scopeId === '' || scopeId === 'me') return undefined
  return { id: String(scopeId), kind }
}

/**
 * cmd `sources` ([`MeetingSource`]). Empty/all → omitted (both internal + external); Calendly only →
 * `[SCHEDULING]` (internal only); Google/Outlook only → `[EXTERNAL]`. `EXTERNAL` covers both connected
 * calendars, so a Calendly+external mix is treated as "both" (omitted).
 */
const buildListMeetingsSources = (schedulingMethods) => {
  if (!Array.isArray(schedulingMethods) || schedulingMethods.length === 0) return undefined
  if (schedulingMethods.includes(SchedulingMethods.ALL)) return undefined
  const hasCalendly = schedulingMethods.includes(SchedulingMethods.CALENDLY)
  const hasExternal = schedulingMethods.includes(SchedulingMethods.GOOGLE)
    || schedulingMethods.includes(SchedulingMethods.OUTLOOK)
  if (hasCalendly && !hasExternal) return ['SCHEDULING']
  if (!hasCalendly && hasExternal) return ['EXTERNAL']
  return undefined
}

/**
 * cmd `listMeetings` has no status enum — only `cancelled` / `hasNoShows` / `hasReschedules` booleans.
 * Map the common single-status selections; mixed active+canceled selections leave `cancelled` unset.
 */
const buildListMeetingsStatusFlags = (statusIds) => {
  if (!Array.isArray(statusIds) || statusIds.length === 0) return {}
  const set = new Set(statusIds)
  const flags = {}
  if (set.has('no_show')) flags.hasNoShows = true
  if (set.has('rescheduled')) flags.hasReschedules = true
  if (set.size === 1) {
    if (set.has('active')) flags.cancelled = false
    else if (set.has('canceled')) flags.cancelled = true
  }
  return flags
}

/** camelCased filter for cmd `listMeetings` (`Meetings_ListMeetingsFilter`). */
const buildListMeetingsFilterFromUiFilters = (uiFilters, { today, past, forCount } = {}) => {
  if (!uiFilters) return null

  const {
    inviteeEmails,
    eventTypeIds,
    role,
    statusIds,
    schedulingMethods,
    scopeType,
    scopeId,
    uuid,
    utmCampaign,
    utmContent,
    utmMedium,
    utmSource,
    utmTerm,
  } = uiFilters

  const filter = {}

  if (inviteeEmails?.length) filter.inviteeEmails = inviteeEmails
  if (eventTypeIds?.length) filter.eventTypeUuids = eventTypeIds
  if (uuid) filter.uuids = [uuid]

  const sources = buildListMeetingsSources(schedulingMethods)
  if (sources) filter.sources = sources

  const scope = buildListMeetingsScope(scopeType, scopeId)
  if (scope) filter.scope = scope

  Object.assign(filter, buildListMeetingsStatusFlags(statusIds))

  if (Array.isArray(role) && role.length) {
    if (role.includes('HOST') || role.includes('COHOST')) {
      filter.isHost = true
    } else if (role.includes('ATTENDEE')) {
      filter.isHost = false
    }
  }

  if (utmCampaign?.length) filter.utmCampaigns = utmCampaign
  if (utmContent?.length) filter.utmContents = utmContent
  if (utmMedium?.length) filter.utmMediums = utmMedium
  if (utmSource?.length) filter.utmSources = utmSource
  if (utmTerm?.length) filter.utmTerms = utmTerm

  const timeFilter = buildSupergraphTimeFilter(uiFilters, { today, past, forCount })
  if (timeFilter) filter.startTime = timeFilter

  return filter
}

/**
 * cmd `listMeetings` input (`Meetings_ListMeetingsInput`) for the default meetings page / scope /
 * non-recap filter list slices.
 */
export const buildSupergraphInput = (uiFilters, { today, past, first, after, order }) => {
  const filter = buildListMeetingsFilterFromUiFilters(uiFilters, { today, past })
  return {
    filter,
    pagination: {
      first,
      after: after || null,
    },
    orderBy: {
      startTime: order,
    },
  }
}

/** Page size for supergraph filter counts — fetch nodes and count the returned meetings array. */
const SUPERGRAPH_COUNT_PAGE_SIZE = 10_000

const COUNT_STATUS_IDS = Object.freeze(['active', 'canceled', 'no_show', 'rescheduled'])

const buildMeetingsPageListCountInput = (uiFilters) => ({
  filter: buildSupergraphFilterFromUiFilters(uiFilters, { forCount: true }),
  pagination: {
    first: SUPERGRAPH_COUNT_PAGE_SIZE,
    after: null,
  },
  orderBy: {
    start_time: 'ASC',
  },
})

const buildListMeetingsCountInput = (uiFilters) => ({
  filter: buildListMeetingsFilterFromUiFilters(uiFilters, { forCount: true }),
  pagination: {
    first: SUPERGRAPH_COUNT_PAGE_SIZE,
    after: null,
  },
  orderBy: {
    startTime: 'ASC',
  },
})

/**
 * cmd `listMeetings` encodes status as booleans, so OR selections (e.g. active + canceled) need one
 * count query per status; the API layer sums the results.
 */
export const buildListMeetingsCountInputs = (uiFilters) => {
  const statusIds = uiFilters?.statusIds
  const distinctStatuses = Array.isArray(statusIds)
    ? [...new Set(statusIds.filter((id) => id && id !== 'all' && COUNT_STATUS_IDS.includes(id)))]
    : []

  if (distinctStatuses.length <= 1) {
    return [buildListMeetingsCountInput(uiFilters)]
  }

  return distinctStatuses.map((statusId) => buildListMeetingsCountInput({
    ...uiFilters,
    statusIds: [statusId],
  }))
}

/**
 * Supergraph filter-count request: route to `listMeetings` (default) or `meetingsPageList` (search/recap).
 * Returns `{ path, inputs }` for the RTK count endpoint (`inputs` is one or more GraphQL variable payloads).
 */
export const buildSupergraphCountRequest = (uiFilters) => {
  if (shouldUseMeetingsPageList(uiFilters)) {
    return {
      path: 'meetingsPageList',
      inputs: [buildMeetingsPageListCountInput(uiFilters)],
    }
  }

  return {
    path: 'listMeetings',
    inputs: buildListMeetingsCountInputs(uiFilters),
  }
}

const lazyArgsStartTimeFilter = (args) => args?.filter?.startTime ?? args?.filter?.start_time

export const doesSupergraphLazyArgsMatchFixedVisibleRange = (originalArgs, visibleRangeUiVars) => {
  if (!visibleRangeUiVars || visibleRangeUiVars.period !== Periods.FIXED) return false
  const expected = buildListMeetingsFilterFromUiFilters(visibleRangeUiVars, {})
  const lazyPageStartTimeFilter = lazyArgsStartTimeFilter(originalArgs)
  const visibleRangeStartTimeFilter = expected?.startTime
  if (!lazyPageStartTimeFilter || !visibleRangeStartTimeFilter) return false
  return lazyPageStartTimeFilter.operation === visibleRangeStartTimeFilter.operation
    && lazyPageStartTimeFilter.start === visibleRangeStartTimeFilter.start
    && lazyPageStartTimeFilter.end === visibleRangeStartTimeFilter.end
}

/**
 * Periods where future vs past queries apply distinct start_time filters.
 * For all_time / past (and unknown), both legs are unbounded and overlap — only past should be merged.
 */
export const usesSplitFuturePastTimeFilters = (period) => (
  period === Periods.UPCOMING
  || period === Periods.FIXED
  || period === Periods.THIS_WEEK
  || period === Periods.THIS_MONTH
  || period === Periods.THIS_YEAR
  || period === Periods.TODAY
)

export const isPreviousPageLazyArgs = (args, isSupergraphEnabled) => {
  if (!args) return false
  if (isSupergraphEnabled) {
    const order = args.orderBy?.startTime ?? args.orderBy?.start_time
    return order === 'DESC'
  }
  return Boolean(args.isPrevious || args.past)
}
